// Copyright 2023 Anthony Howell. All rights reserved.

import { Card, GameType, SimulationResultPercentages, GameTypeExtensions } from './types';
import { GameLogic } from './GameLogic';

class SimulationModel {
  simulationResults: SimulationResultPercentages[] = [];
  isSimulating = false;
  numberOfSimulations: number;
  deck: Card[];
  playersCards: Card[][];
  boardCards: Card[];
  numberOfPlayers: number;
  selectedGameType: GameType;
  maxBoardCards = 5;

  constructor(
    deck: Card[],
    playersCards: Card[][],
    boardCards: Card[],
    numberOfOpponents: number,
    selectedGameType: GameType,
    numberOfSimulations: number
  ) {
    this.deck = deck;
    this.playersCards = playersCards;
    this.boardCards = boardCards;
    this.numberOfPlayers = numberOfOpponents;
    this.selectedGameType = selectedGameType;
    this.numberOfSimulations = numberOfSimulations;
  }

  async runSimulations(): Promise<SimulationResultPercentages[]> {
    const anyPlayersCardsAreBlank = this.playersCards.some((cards) => cards.some((card) => card.rank === 0));

    const highSimulationResults: number[][][] = [];
    const lowSimulationResults: number[][][] = [];
    const highHandStrengths: number[][] = [];
    const lowHandStrengths: number[][] = [];

    const originalBoard = [...this.boardCards];
    const originalDeck = [...this.deck];
    const boardCardCount = this.boardCards.filter((card) => card.rank !== 0).length;

    const appendSimulationResults = (
      highResults: number[][],
      lowResults: number[][],
      highStrengths: number[],
      lowStrengths: number[]
    ) => {
      highSimulationResults.push(highResults);
      lowSimulationResults.push(lowResults);
      highHandStrengths.push(highStrengths);
      lowHandStrengths.push(lowStrengths);
    };

    if (anyPlayersCardsAreBlank) {
      for (let i = 0; i < this.numberOfSimulations; i++) {
        if (boardCardCount === 5) {
          this.boardCards = originalBoard;
        } else {
          this.boardCards = [...originalBoard.slice(0, boardCardCount), ...this.getRandomBoard(this.maxBoardCards - boardCardCount)];
        }
        const { highResults, lowResults, highStrengths, lowStrengths } = await this.simulateOneRound();
        appendSimulationResults(highResults, lowResults, highStrengths, lowStrengths);
        this.resetBoardAndDeck(originalBoard, originalDeck);
      }
    } else {
      const remainingDeck = this.getRemainingDeck();

      if (boardCardCount === 5) {
        const { highResults, lowResults, highStrengths, lowStrengths } = await this.simulateOneRound();
        appendSimulationResults(highResults, lowResults, highStrengths, lowStrengths);
      } else if (boardCardCount === 3) {
        for (let i = 0; i < remainingDeck.length - 1; i++) {
          for (let j = i + 1; j < remainingDeck.length; j++) {
            this.boardCards = [...originalBoard.slice(0, 3), remainingDeck[i], remainingDeck[j]];
            const { highResults, lowResults, highStrengths, lowStrengths } = await this.simulateOneRound();
            appendSimulationResults(highResults, lowResults, highStrengths, lowStrengths);
            this.resetBoardAndDeck(originalBoard, originalDeck);
          }
        }
      } else if (boardCardCount === 4) {
        for (const card of remainingDeck) {
          this.boardCards = [...originalBoard.slice(0, 4), card];
          const { highResults, lowResults, highStrengths, lowStrengths } = await this.simulateOneRound();
          appendSimulationResults(highResults, lowResults, highStrengths, lowStrengths);
          this.resetBoardAndDeck(originalBoard, originalDeck);
        }
      } else {
        for (let i = 0; i < this.numberOfSimulations; i++) {
          this.boardCards = this.getRandomBoard(this.maxBoardCards);
          const { highResults, lowResults, highStrengths, lowStrengths } = await this.simulateOneRound();
          appendSimulationResults(highResults, lowResults, highStrengths, lowStrengths);
          this.resetBoardAndDeck(originalBoard, originalDeck);
        }
      }
    }

    const totalSimulations = highSimulationResults.length;
    this.simulationResults = this.calculateAndStoreResults(
      highSimulationResults,
      lowSimulationResults,
      highHandStrengths,
      lowHandStrengths,
      totalSimulations
    );

    return this.simulationResults;
  }

  async simulateOneRound(): Promise<{
    highResults: number[][];
    lowResults: number[][];
    highStrengths: number[];
    lowStrengths: number[];
  }> {
    const localPlayersCards = this.playersCards.map((playerHand) =>
      playerHand.map((card) => (card.rank === 0 ? this.getRandomCard() : card))
    );

    const gameLogic = new GameLogic(this.deck, localPlayersCards, this.boardCards, this.selectedGameType);
    const [highResults, lowResults, highStrengths, lowStrengths] = gameLogic.determineWinnersAndTies();

    return {
      highResults,
      lowResults,
      highStrengths,
      lowStrengths,
    };
  }

  private getRandomCard(): Card {
    const remainingDeck = this.getRemainingDeck();
    const randomIndex = Math.floor(Math.random() * remainingDeck.length);
    return remainingDeck[randomIndex];
  }

  calculateAndStoreResults(
    highSimulationResults: number[][][],
    lowSimulationResults: number[][][],
    highHandStrengths: number[][],
    lowHandStrengths: number[][],
    totalSimulations: number
  ): SimulationResultPercentages[] {
    const results: SimulationResultPercentages[] = [];
  
    for (let playerIndex = 0; playerIndex < this.playersCards.length; playerIndex++) {
      const highResult = this.sumSimulationResults(highSimulationResults, playerIndex);
      const highWinPercentage = (highResult[0] / totalSimulations) * 100;
      const highTiePercentage = (highResult[1] / totalSimulations) * 100;
      const playerHighHandStrengths = highHandStrengths.map((strengths) => strengths[playerIndex]);
  
      let lowWinPercentage = 0;
      let lowTiePercentage = 0;
      let playerLowHandStrengths: number[] = [];
  
      if (GameTypeExtensions[this.selectedGameType].isLowGame) {
        const lowResult = this.sumSimulationResults(lowSimulationResults, playerIndex);
        lowWinPercentage = (lowResult[0] / totalSimulations) * 100;
        lowTiePercentage = (lowResult[1] / totalSimulations) * 100;
        playerLowHandStrengths = lowHandStrengths.map((strengths) => strengths[playerIndex]);
      }
  
      const highEquity = highWinPercentage + highTiePercentage / 2;
      const lowEquity = lowWinPercentage + lowTiePercentage / 2;
  
      const simulationResult: SimulationResultPercentages = {
        highWins: highWinPercentage,
        highTies: highTiePercentage,
        lowWins: lowWinPercentage,
        lowTies: lowTiePercentage,
        highHandStrengths: playerHighHandStrengths,
        lowHandStrengths: playerLowHandStrengths,
        highEquity,
        lowEquity,
        overallEquity: 0, // We'll calculate this later
        highHandCardDistributionPercentages: this.calculateHandDistributionPercentages(playerHighHandStrengths, totalSimulations),
      };
  
      results.push(simulationResult);
    }
  
    // Calculate overallEquity for each player
    const totalHighEquity = results.reduce((sum, result) => sum + result.highEquity, 0);
    const totalLowEquity = GameTypeExtensions[this.selectedGameType].isLowGame
      ? results.reduce((sum, result) => sum + result.lowEquity, 0)
      : 0;
    const totalEquity = totalHighEquity + totalLowEquity;
  
    results.forEach((result) => {
      const playerHighEquity = result.highEquity;
      const playerLowEquity = GameTypeExtensions[this.selectedGameType].isLowGame ? result.lowEquity : 0;
      result.overallEquity = ((playerHighEquity + playerLowEquity) / totalEquity) * 100;
    });
  
    return results;
  }

  private calculateHandDistributionPercentages(
    handStrengths: number[],
    totalSimulations: number
  ): { category: string; percentage: number }[] {
    const handCategories: [number, number, string][] = [
      [9000000, 9999999, "Straight Flush"],
      [8000000, 8999999, "Four of a Kind"],
      [7000000, 7999999, "Full House"],
      [6000000, 6999999, "Flush"],
      [5000000, 5999999, "Straight"],
      [4000000, 4999999, "Three of a Kind"],
      [3000000, 3999999, "Two Pair"],
      [2000000, 2999999, "Pair"],
      [1000000, 1999999, "High Card"],
    ];

    const counts: { [category: string]: number } = {};

    for (const strength of handStrengths) {
      for (const [min, max, category] of handCategories) {
        if (strength >= min && strength <= max) {
          counts[category] = (counts[category] || 0) + 1;
          break;
        }
      }
    }

    const percentages = handCategories.map(([_, __, category]) => {
      const count = counts[category] || 0;
      const percentage = (count / handStrengths.length) * 100;
      return { category, percentage };
    });

    return percentages;
  }

  getRandomBoard(missingCards: number): Card[] {
    const remainingDeckCopy = [...this.getRemainingDeck()];
    const board: Card[] = [];

    while (board.length < missingCards) {
      const randomIndex = Math.floor(Math.random() * remainingDeckCopy.length);
      const randomCard = remainingDeckCopy[randomIndex];
      if (randomCard) {
        board.push(randomCard);
        remainingDeckCopy.splice(randomIndex, 1);
      }
    }

    return board;
  }

  generateRandomHand(): Card[] {
    const remainingDeck = this.getRemainingDeck();
    const randomHand: Card[] = [];
    const handSize = GameTypeExtensions[this.selectedGameType].maxPlayersCards;

    while (randomHand.length < handSize) {
      const randomCard = remainingDeck[Math.floor(Math.random() * remainingDeck.length)];
      if (randomCard) {
        randomHand.push(randomCard);
        remainingDeck.splice(
          remainingDeck.findIndex((card) => card.id === randomCard.id),
          1
        );
      }
    }

    return randomHand;
  }

  private resetBoardAndDeck(originalBoard: Card[], originalDeck: Card[]) {
    this.boardCards = originalBoard.slice();
    this.deck = originalDeck.slice();
  }

  private sumSimulationResults(simulationResults: number[][][], playerIndex: number): number[] {
    const sumResults = [0, 0];

    for (const simulation of simulationResults) {
      const result = simulation[playerIndex] || [0, 0];
      sumResults[0] += result[0];
      sumResults[1] += result[1];
    }

    return sumResults;
  }

  private getRemainingDeck(): Card[] {
    return this.deck.filter(
      (card) =>
        !this.playersCards.some((hand) => hand.some((playerCard) => playerCard.id === card.id)) &&
        !this.boardCards.some((boardCard) => boardCard.id === card.id)
    );
  }
}


export { SimulationModel }